import { z } from 'zod';

/**
 * Bytes per stored graph chunk. The writer and every inflated read share this
 * contract so neither side can silently accept a graph the other rejects.
 */
export const MAX_SCAN_CHUNK_BYTES = 15_000_000;

/** Maximum chunks in one stored scan graph. Rejected before any blob fetch. */
export const MAX_SCAN_CHUNKS = 8;

/** New writes stay below the verification runtime's compressed allocation. */
export const MAX_NEW_SCAN_CHUNKS = 2;

/** Maximum compressed bytes accepted by a scan reader. */
export const MAX_COMPRESSED_SCAN_BYTES: number =
  MAX_SCAN_CHUNK_BYTES * MAX_SCAN_CHUNKS;

/**
 * Maximum inflated JSON bytes accepted by a scan reader.
 *
 * Real cached scans measured an 18–19x gzip ratio. At the documented 8 MB
 * production size that projects to roughly 155 MB inflated; twice the absolute
 * compressed ceiling leaves about 55% headroom while bounding bytes at 240 MB
 * before UTF-8 decoding and `JSON.parse` add transient allocations.
 */
export const MAX_INFLATED_SCAN_BYTES: number = MAX_COMPRESSED_SCAN_BYTES * 2;

/**
 * Inflated JSON admitted by the current writer and upload verifier.
 *
 * Verification concurrently retains compressed input, decoded text, the
 * parsed graph and a canonical digest encoding. Capping the source JSON at
 * 48 MB and new compressed input at 30 MB keeps those representations within
 * a 384 MB working-set budget while leaving headroom in the Convex action.
 * The larger constants above remain read compatibility bounds for historical
 * graphs; new uploads never use them.
 */
export const MAX_NEW_SCAN_INFLATED_BYTES = 48_000_000;

// ---------------------------------------------------------------------------
// The read shape of an Impact-Engine repo scan.
//
// Validated on the way OUT as well as in: the `summary` column is a JSON string
// written by the scan task, so the reader cannot assume it matches this shape.
// A scan recorded by an older task, or one whose projection has since changed,
// must degrade to "no usable summary" rather than reaching a caller as a
// half-typed object.
//
// Deliberately absent: any graph URL. Convex storage URLs are unauthenticated
// capability URLs that outlive the request that minted them, so the graph is a
// separate, explicitly-authenticated download (`/api/github/repo-scan/graph`).
// ---------------------------------------------------------------------------

export const RepoScanHealthSchema = z.enum(['strong', 'mixed', 'fragile']);

/**
 * `owner/name`. Shared by both routes so they reject the same values — a
 * summary that accepts a shape the graph route refuses (or vice versa) is a
 * surface that disagrees with itself about what a repo is.
 */
export const RepoSlugSchema = z
  .string()
  .regex(/^[\w.-]+\/[\w.-]+$/, 'expected owner/name');

/** A full 40-character git object id; the scan records nothing shorter. */
export const CommitShaSchema = z.string().regex(/^[\da-f]{40}$/);

/** Present-signal flags. Two scans that measured different things must never be
 * compared as though they measured the same ones. */
export const RepoScanSignalsSchema = z.object({
  coverage: z.boolean(),
  staticAnalysis: z.boolean(),
  /** Python source was ANALYSED — not "this repo contains Python". */
  python: z.boolean(),
  /**
   * Django apps were detected in the graph. A repo fact, not a coverage
   * claim. Optional: absent means the scan predates the split, which is a
   * different statement from "no Django apps".
   */
  django: z.boolean().optional(),
});

/**
 * Nullable throughout: absent measurement is never reported as zero.
 *
 * Bounded on the way OUT as well as in. A row written before these bounds
 * existed, or by a future writer that skips them, must degrade to "unreadable"
 * rather than reach a caller as a 4000% review rate.
 */
const NullableCount = z.number().nonnegative().nullable();
const NullablePercent = z.number().min(0).max(100).nullable();

/**
 * One static-analysis finding as the scan row stores it (#2639).
 *
 * Defined HERE, in the contract both the writer and the upload boundary
 * import, rather than restated at each. Two copies of a shape are two chances
 * for the row and the parser to disagree about what a valid finding is.
 */
const StoredFindingV1Schema = z.object({
  ruleId: z.string().min(1).max(128),
  /** REPO-RELATIVE; `buildFindingDigest` drops anything else. */
  path: z.string().min(1).max(1024),
  severity: z.enum(['error', 'warning', 'info']),
  category: z.enum([
    'lint',
    'security',
    'complexity',
    'dead-code',
    'vulnerability',
    'best-practice',
    'type-safety',
  ]),
});

const SuppressionScopeSchema = z.enum([
  'line',
  'next-line',
  'block',
  'file',
  'ambiguous',
]);

export const StoredFindingSchema = StoredFindingV1Schema.extend({
  line: z.number().int().positive().optional(),
  column: z.number().int().positive().optional(),
  message: z.string().max(200).optional(),
  suppressionScope: SuppressionScopeSchema.optional(),
});

export const DebtMarkerSchema = z.object({
  path: z.string().min(1).max(1024),
  line: z.number().int().positive(),
  excerpt: z.string().min(1).max(200),
  ageDays: z.number().int().nonnegative().optional(),
  issueLink: z.string().min(1).max(512).optional(),
});

export const SuppressionEvidenceSchema = z.object({
  ruleId: z.string().min(1).max(128),
  path: z.string().min(1).max(1024),
  line: z.number().int().positive(),
  scope: SuppressionScopeSchema,
});

const FindingDigestBaseSchema = z.object({
  /** PRE-cap. `findings.length` is what fitted; this is what existed. */
  total: z.number().int().nonnegative(),
  truncated: z.boolean(),
});

const FindingDigestV1Schema = FindingDigestBaseSchema.extend({
  version: z.literal(1),
  findings: z.array(StoredFindingV1Schema),
});

const FindingDigestV2Schema = FindingDigestBaseSchema.extend({
  version: z.literal(2),
  findings: z.array(StoredFindingSchema),
  /**
   * Distinct rule-and-file fingerprints sitting on a suppression directive —
   * not a directive count and not a file count. See the field's contract on
   * `FindingDigest` in `engine/scanners/finding-digest.ts`; readers must not
   * present it as a suppression inventory.
   */
  suppressionTotal: z.number().int().nonnegative().optional(),
  suppressionsTruncated: z.boolean().optional(),
  suppressions: z.array(SuppressionEvidenceSchema).optional(),
  debtMarkerTotal: z.number().int().nonnegative(),
  debtMarkersTruncated: z.boolean(),
  debtMarkers: z.array(DebtMarkerSchema),
}).superRefine((digest, context) => {
  const fields = [
    digest.suppressionTotal,
    digest.suppressionsTruncated,
    digest.suppressions,
  ];
  const present = fields.filter((field) => field !== undefined).length;
  if (present !== 0 && present !== fields.length) {
    context.addIssue({
      code: 'custom',
      message: 'Suppression evidence fields must be all present or all absent',
    });
    return;
  }
  if (
    digest.suppressionTotal === undefined ||
    digest.suppressionsTruncated === undefined ||
    digest.suppressions === undefined
  )
    return;
  const truncated = digest.suppressions.length < digest.suppressionTotal;
  if (
    digest.suppressions.length > digest.suppressionTotal ||
    digest.suppressionsTruncated !== truncated
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Suppression evidence count and truncation are inconsistent',
    });
  }
});

/** V1 remains parseable, but readers must require V2 before using hygiene evidence. */
export const FindingDigestSchema = z.union([
  FindingDigestV1Schema,
  FindingDigestV2Schema,
]);
export type FindingDigest = z.infer<typeof FindingDigestSchema>;

export const RepoScanReviewHealthSchema = z
  .object({
    pullRequestCount: z.number().int().nonnegative(),
    mergedCount: z.number().int().nonnegative(),
    medianHoursToMerge: NullableCount,
    p90HoursToMerge: NullableCount,
    reviewedPercent: NullablePercent,
    approvedPercent: NullablePercent,
    medianReviewers: NullableCount,
    medianComments: NullableCount,
    medianChangedFiles: NullableCount,
    medianLinesChanged: NullableCount,
  })
  // Independently valid counts can still be jointly impossible: 50 merged out
  // of 10 pull requests passes both `nonnegative().int()` checks and cannot
  // have come from a real window.
  .refine(
    // Annotated readonly because the Zod-inferred argument is mutable, which
    // prefer-readonly-parameter-types rejects.
    (health: Readonly<{ mergedCount: number; pullRequestCount: number }>) =>
      health.mergedCount <= health.pullRequestCount,
    { message: 'mergedCount cannot exceed pullRequestCount' },
  );

export type RepoScanReviewHealth = z.infer<typeof RepoScanReviewHealthSchema>;

export const RepoScanSummarySchema = z.object({
  overallHealth: RepoScanHealthSchema,
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  cycleCount: z.number().int().nonnegative(),
});

export const RepoScanRunStatusSchema = z.enum([
  'queued',
  'running',
  'succeeded',
  'failed',
  'skipped',
]);

/** The latest run, which is how a caller sees a scan that is queued or failed —
 * `repo_scans` only ever holds scans that completed. */
/** Bound mirrored from `slowestPhases`, so the contract states the cap too. */
export const MAX_PERSISTED_PHASES = 8;
export const MAX_PHASE_NAME_LENGTH = 64;

export const PhaseTimingSchema = z.object({
  phase: z.string().min(1).max(MAX_PHASE_NAME_LENGTH),
  ms: z.number().nonnegative(),
});

export const RepoScanRunSchema = z.object({
  status: RepoScanRunStatusSchema,
  reason: z.string().nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  /**
   * What was ASKED for. Distinct from the resulting `graphFidelity`: a `full`
   * request whose graph overflowed still stores `reduced`.
   */
  scanProfile: z.enum(['fast', 'full']).nullable().default(null),
  /**
   * Where the wall time went, slowest first. Empty when the run recorded
   * nothing — a refusal, or a scan from before timings existed.
   */
  phaseTimings: z
    .array(PhaseTimingSchema)
    .max(MAX_PERSISTED_PHASES)
    .default([]),
  /** End-to-end wall time. `null` for a run that never completed a scan. */
  totalMs: z.number().nonnegative().nullable().default(null),
  /**
   * Sum of the phases, which is NOT `totalMs`: phases do not cover the gaps
   * between them, so a large difference is itself the finding.
   */
  phaseTotalMs: z.number().nonnegative().nullable().default(null),
});

export const RepoScanSnapshotSchema = z.object({
  repo: RepoSlugSchema,
  /** The commit that was scanned. Without it no result can be reproduced. */
  headSha: CommitShaSchema,
  defaultBranch: z.string().min(1),
  scannedAt: z.iso.datetime(),
  overallHealth: RepoScanHealthSchema,
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  cycleCount: z.number().int().nonnegative(),
  signalsPresent: RepoScanSignalsSchema,
  /** `full` unless file nodes were dropped to fit the upload budget. */
  graphFidelity: z.enum(['full', 'reduced']),
  /** Set when the graph itself was not stored; the summary still is. */
  graphOmittedReason: z.string().nullable(),
  /** True when a graph blob exists to download. */
  graphAvailable: z.boolean(),
  /**
   * How the repository reviews its changes, from GitHub pull-request data.
   *
   * `null` when the scan did not measure it — an org whose PR mirror has never
   * synced. NOT the same as a repo with no reviews, and every field inside is
   * separately nullable for the same reason: a window with nothing merged has
   * no cycle time, and a 0 would rank it as instant.
   */
  reviewHealth: RepoScanReviewHealthSchema.nullable().default(null),
});

export type RepoScanSnapshot = z.infer<typeof RepoScanSnapshotSchema>;
export type RepoScanRun = z.infer<typeof RepoScanRunSchema>;
export type RepoScanHealth = z.infer<typeof RepoScanHealthSchema>;

/**
 * What `GET /api/github/repo-scan` answers with.
 *
 * Exported so the client validates it rather than asserting it: a browser
 * response is external input even when our own server produced it, and an `as`
 * cast would let a shape change reach React as a runtime crash instead of a
 * handled error.
 */
export const RepoScanResponseSchema = z.object({
  connected: z.boolean(),
  scan: RepoScanSnapshotSchema.nullable().default(null),
  run: RepoScanRunSchema.nullable().default(null),
});

/**
 * What `POST /api/github/repo-scan` answers with.
 *
 * The run id, plus what the caller's ref resolved to when the start path could
 * resolve it. `resolvedSha: null` is honest rather than empty: an organisation
 * scan resolves its ref inside the task, and the caller reads the commit back
 * from the capture that run produces.
 */
export const RepoScanQueuedSchema = z.object({
  id: z.string().min(1),
  resolvedSha: z.string().min(1).nullable().default(null),
  requestedRef: z.string().min(1).nullable().default(null),
});

/** Every typed refusal emitted by `POST /api/github/repo-scan`. */
export const RepoScanStartErrorResponseSchema = z.object({
  error: z.enum([
    'forbidden',
    'scan_not_configured',
    'upstream',
    'repo_not_visible',
    'credential_rejected',
    'repo_too_large',
    'scan_allowance_exhausted',
    'unknown_ref',
    'revision_unresolved',
    'invalid_evidence_profile',
  ]),
});

export type RepoScanStartErrorResponse = z.infer<
  typeof RepoScanStartErrorResponseSchema
>;

/** The authentication failure shared by the repo-scan HTTP surfaces. */
export const RepoScanAuthenticationErrorResponseSchema = z.object({
  error: z.literal('unauthenticated'),
});

export type RepoScanResponse = z.infer<typeof RepoScanResponseSchema>;

/**
 * The evidence-download response.
 *
 * A runtime contract, not just an internal shape: this is an API response, and
 * it is built from a decompressed blob whose contents nothing guarantees.
 */
export const EvidenceProvenanceSchema = z.enum([
  'git-log',
  'merge-commit',
  /** Joined to GitHub review rows — carries review depth for this node. */
  'mirror-joined',
  /** PRs are known for this node, but no review data could be joined to them. */
  'clone-only',
  'other',
]);

export const NodeEvidenceRowSchema = z.object({
  nodeId: z.string(),
  status: z.string(),
  sources: z.array(
    z.object({
      provenance: EvidenceProvenanceSchema,
      description: z.string(),
      confidence: z.string(),
    }),
  ),
});

export const RepoScanEvidenceSchema = z.object({
  repo: RepoSlugSchema,
  headSha: CommitShaSchema,
  scannedAt: z.iso.datetime(),
  /** What this view can and cannot see, stated rather than implied. */
  provenance: z.object({
    gitLogAggregate: z.literal('complete-for-window'),
    /**
     * Derived from the stored graph, not asserted.
     *
     * `none-recorded` covers two cases a reader must not tell apart from here:
     * a repository that merges by rebase (invisible to the clone), and a scan
     * recorded before the harvest worked at all — every graph stored prior to
     * the `--diff-merges` fix carries no PR signal whatsoever. Claiming a floor
     * in either case would report zero reviewed changes as a measurement.
     */
    mergeCommitPrs: z.enum([
      'merge-commits-only-misses-rebase',
      'none-recorded',
    ]),
    commitLevelFileLists: z.literal('not-collected'),
    reviewDepthAndCycleTime: z.enum([
      'per-node',
      'repo-level-only',
      'not-collected',
    ]),
  }),
  reviewHealth: RepoScanReviewHealthSchema.nullable().default(null),
  counts: z.object({
    nodes: z.number().int().nonnegative(),
    withEvidence: z.number().int().nonnegative(),
    withPrSignal: z.number().int().nonnegative(),
    /** Nodes carrying joined review data. */
    withReviewAttribution: z.number().int().nonnegative(),
    /** Nodes with PRs but no joinable review data — measured NOTHING, not zero. */
    withCloneOnlyReviews: z.number().int().nonnegative(),
    byStatus: z.record(z.string(), z.number().int().nonnegative()),
  }),
  nodes: z.array(NodeEvidenceRowSchema),
});

/** Every non-success payload returned by the evidence-download endpoint. */
export const RepoScanEvidenceErrorResponseSchema = z.object({
  error: z.enum([
    'unauthenticated',
    'org_and_repo_required',
    'invalid_org',
    'invalid_repo',
    'not_found',
    'graph_not_stored',
    'unconfigured',
    'graph_unreadable',
  ]),
});

/**
 * The stored graph, read permissively.
 *
 * Deliberately loose: this parses a blob the scan wrote, and a shape we do not
 * recognise must degrade to "unreadable" rather than crashing the route. Only
 * the fields the evidence projection actually reads are described.
 */
export const StoredGraphSchema = z.object({
  serializedGraph: z
    .object({
      nodes: z.array(z.unknown()).default([]),
    })
    .default({ nodes: [] }),
});

export type RepoScanEvidenceResponse = z.infer<typeof RepoScanEvidenceSchema>;
export type RepoScanEvidenceError = z.infer<
  typeof RepoScanEvidenceErrorResponseSchema
>['error'];
